import { Layout } from "@visma-netvisor/react-component-library";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { sendPurchaseEvent, sendTag } from "../../analytics";
import { sendGAPurchase } from "../../googleAnalytics";
import { strings } from "../../constants";
import { getOrderCookieData } from "../../utils/getOrderCookieData";
import { logFrontendError } from "../LogFrontendError";
import { useCreateOrder, OrderCreationError } from "../hooks/useCreateOrder";
import CreationStatusContent from "./CreationStatusContent";
import CreationStatusHeader from "./CreationStatusHeader";

const StatusContent = () => {
  const [companyAlreadyExist, setCompanyAlreadyExist] = useState(false);
  const [activationError, setActivationError] = useState(false);
  const [authExpired, setAuthExpired] = useState(false);
  const [companyCreatedSuccess, setCompanyCreatedSuccess] = useState(
    sessionStorage.getItem("companyCreationSuccess") === "1"
  );
  const { encodedOrderData, decodedOrderData } = getOrderCookieData();

  const orderId = sessionStorage.getItem(strings.ORDER_ID_COOKIE_NAME) || "";
  const [companyCreationStatus, setCompanyCreationStatus] = useState<
    number | null | undefined
  >(orderId ? strings.CREATION_STATUS_ENUMS.READY_FOR_PROCESSING : null);

  const useCreateOrderMutation = useCreateOrder();
  const hasCreatedRef = useRef(false);

  const createCompany = useCallback(async () => {
    try {
      await useCreateOrderMutation.mutateAsync({
        order: encodedOrderData,
        setCompanyAlreadyExist: setCompanyAlreadyExist,
      });
      setCompanyCreationStatus(
        strings.CREATION_STATUS_ENUMS.READY_FOR_PROCESSING
      );
      sendTag({ status: "authenticationSuccess" });
      sendPurchaseEvent();
      sendGAPurchase();
    } catch (error) {
      const isOrderError = error instanceof OrderCreationError;
      await logFrontendError({
        info: "Order creation failed",
        orderId,
        organizationIdentifier: decodedOrderData?.OrganizationIdentifier,
        status: isOrderError ? error.status : undefined,
        errorCode: isOrderError ? error.errorCode : undefined,
        backendStatus: isOrderError ? error.backendStatus : undefined,
        backendErrorCode: isOrderError ? error.backendErrorCode : undefined,
        error,
      });
      // 401 indicates missing Telia auth claims (typically from an earlier "Correlation failed" callback); route to the recoverable re-auth UI instead of the generic error screen.
      if (isOrderError && error.status === 401) {
        setAuthExpired(true);
      } else {
        setActivationError(true);
      }
    }
  }, [useCreateOrderMutation, encodedOrderData]);

  useEffect(() => {
    if (hasCreatedRef.current) return;
    if (
      companyCreationStatus === null &&
      encodedOrderData &&
      decodedOrderData
    ) {
      hasCreatedRef.current = true;
      createCompany();
    }
  }, []);

  return (
    <CustomLayout>
      <GridContainer>
        <CreationStatusHeader
          isError={companyAlreadyExist || activationError}
          formFirstName={decodedOrderData?.FirstName}
        />
        <Layout.Item as="header">
          <CreationStatusContent
            tradeRegistrationYear={decodedOrderData?.TradeRegistrationYear}
            orderId={orderId}
            activationError={activationError}
            authExpired={authExpired}
            organizationIdentifier={decodedOrderData?.OrganizationIdentifier}
            companyAlreadyExist={companyAlreadyExist}
            companyCreatedSuccess={companyCreatedSuccess}
            setCompanyAlreadyExist={setCompanyAlreadyExist}
            setActivationError={setActivationError}
            setCompanyCreatedSuccess={setCompanyCreatedSuccess}
          />
        </Layout.Item>
      </GridContainer>
    </CustomLayout>
  );
};

const CustomLayout = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: auto;
  height: 100%;

  @media (max-width: 768px) {
    width: 95%;
    margin: 10px;
  }
`;

const GridContainer = styled.div`
  display: grid;
  gap: 16px;
  padding: 24px;
  max-width: 1015px;
  width: 100%;
  height: 422px;
  background-color: #ffffff;
  border-radius: 10px;

  @media (max-width: 768px) {
    gap: 0px;
    padding: 16px;
    height: 350px;
  }
`;

export default StatusContent;
